import Vue from 'vue'
import router from '@/router'
import store from '@/store'
import { i18n } from '@/i18n'
import ErrorHandler from '@/util/ErrorHandler'
import UrlHelper from '@/util/Url'
import { VueOrm, VueHttpAdapter } from '@/orm'
import VueI18n from 'vue-i18n'
import VerofreshGtag from '@/plugins/verofresh-gtag'
import '@/assets/scss/app.scss'
import vuetify from '@/plugins/vuetify'
import './registerServiceWorker'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Vuelidate from 'vuelidate'
import * as VueGoogleMaps from 'vue2-google-maps'
import VueSocketio from 'vue-socket.io-extended'
import io from 'socket.io-client'
import { get } from 'lodash'
import App from '@/App.vue'
import IfSet from '@/components/shared/IfSet.vue'
import TinyMCE from '@/plugins/tinymce'
import C3 from '@/plugins/c3'
import AuthUtil from '@/util/AuthUtil'
import ErrorMixin from '@/components/mixins/ErrorMixin'
import NewRelic from '@/plugins/newrelic'

// Register components
Vue.config.errorHandler = ErrorHandler.handler
Vue.component('app', App)
Vue.component('if-set', IfSet)
Vue.component('font-awesome-icon', FontAwesomeIcon)

// Apply ErrorMixin to every component
Vue.mixin(ErrorMixin)

Vue.use(router, {}, store, AuthUtil)
Vue.use(NewRelic, {}, store);
Vue.use(C3)
Vue.use(VueOrm)
Vue.use(VueHttpAdapter)
Vue.use(Vuelidate)
Vue.use(VueI18n)
Vue.use(TinyMCE)
Vue.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyAv5qZH0jl0EWzlyrBazBAbt1R9iacSkPI',
    // Required to use the Autocomplete plugin
    libraries: 'drawing,geometry,places'
  },

  // If you want to manually install components, e.g.
  // import {GmapMarker} from 'vue2-google-maps/src/components/marker'
  // Vue.component('GmapMarker', GmapMarker)
  // then disable the following:
  installComponents: true
})

Vue.use(
  VueSocketio,
  io(UrlHelper.getSocketUrl()),
  { store: store }
)

// Configure google analytics to use the stage specific tracking code, and
// cookie domain. Note that passing in the router as we do below enables
// automatic pageview tracking (executed via router.afterEach) using the
// pageTrackerTemplate template supplied
Vue.use(VerofreshGtag, {
  config: {
    id: process.env.VUE_APP_GOOGLE_ANALYTICS_TRACKING_ID,
    params: { cookie_domain: process.env.VUE_APP_GOOGLE_ANALYTICS_COOKIE_DOMAIN }
  },
  pageTrackerTemplate: VerofreshGtag.pageTrackerTemplate
}, router, store)

if (process.env.NODE_ENV === 'development') {
  Vue.config.devtools = true
  Vue.config.productionTip = false
} else {
  Vue.config.devtools = false
}

// Our app is never served from www.verofresh.com (that's our marketing URL).
// But some users are quite fixed on the idea that all websites must start with
// www. This leads to them trying things like www.app.verofresh.com or
// www.mb.verofresh.com. When they do this it breaks SSL (since we don't have a
// cert that matches that domain) and subsequently the site stops working too
// (since XHR won't allow loading insecure content/invalid cert). Therefore if
// we find the user has requested a site starting with www then strip it off and
// redirect.
if (window.location.host.match(/^www\./)) {
  const correctHost = window.location.host.replace(/^www\./, '')
  window.location.replace(`${window.location.protocol}//${correctHost}`)
  // This is only really here to halt the remainder of the script execution
  throw new Error(`Invalid host ${window.location.host} requested. Redirecting to ${correctHost}`)
}

// Try to load the the previous locale state if we can (this eliminates a
// "flicker" when we reload the page)
if (get(store, 'getters.localeModule/locale')) {
  i18n.locale = store.getters['localeModule/locale']
}

if (get(store, 'getters.localeModule/translationStrings')) {
  i18n.setLocaleMessage(store.getters['localeModule/locale'], store.getters['localeModule/translationStrings'])
}

// ESlint doesn't want vue defined and not used (but it is used by other files
// loaded after this)
/* eslint-disable-next-line */
const vue = new Vue({
  router,
  store,
  i18n,
  vuetify,
  render: h => h(App)
}).$mount('#app')
